import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import type { TableProps } from '@app/src/components/Table'
import Table from '@app/src/components/Table'
import { getResponseExportColumnsTransparency } from '@app/src/export-columns/response'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import { useHasInquiriesWithProduct } from '@app/src/hooks/useHasInquiriesWithProduct'
import ProviderResponsesFilters from '@app/src/pages/ResourceCollection/Filters/ProviderResponsesFilters'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { UseSort } from '@app/src/types/filter'
import type { Response } from '@app/src/types/resourceExplorer'
import { ResourceTypes } from '@app/src/wf-constants'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface BaseProviderResponseSceneProps {
  RowComponent: TableProps<Response>['RowComponent']
  HeaderComponent: React.FC<{
    toggleSorting: UseSort['toggleSorting']
    activeSorting: UseSort['sorting']
    showTargetProductColumn?: boolean
  }>
  tabs?: ResourceCollectionSceneProps['tabs']
  actionButtons?: ResourceCollectionSceneProps['actionButtons']
  relatedContentFilter?: FilterGroup[]
}

const allowedFilters = [
  'request.template.id',
  'request.subscriptions.creatorOrganization.id',
  'request.subscriptions.creatorOrganization.name',
  'draftStatus',
  'request.creatorOrganizationId',
  'externalVerificationStatus',
  'externalVerificationComment',
  'submittedAt',
  'request.subscriptions.dueAt',
  'request.creatorUserId',
]

const BaseProviderResponseScene: React.FC<BaseProviderResponseSceneProps> = ({
  RowComponent,
  HeaderComponent,
  tabs,
  actionButtons = [],
  relatedContentFilter = [],
}) => {
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  const apiFilters = useGetApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery(undefined, [apiFilters])
  const { openDrawer } = useDrawer()

  const basePayload = {
    filter: [...relatedContentFilter],
    sort: sorting,
    include: [
      'creatorOrganization',
      'request.creatorOrganization',
      'request.responderOrganization',
      'request.template',
      'request.template.image',
      'request.target',
      'request.subscriptions.creatorOrganization',
      'verifications',
      'request.subscriptions.targetProduct',
    ],
  }

  const payload = {
    ...basePayload,
    include: [...basePayload.include],
    filter: [...basePayload.filter, ...apiFilters],
    pagination: {
      pageNumber: page,
      itemsPerPage: pageSize,
    },
  }

  const { items, count, isLoading, isFetching, isError } = useFetchCollectionWithPost<Response>({
    key: FetchKey.Response,
    endpoint: endpoints.responsesCollection,
    payload,
  })

  const hasInquiriesWithProduct = useHasInquiriesWithProduct()

  const showTargetProductColumn = useMemo(() => items.some(response => response.request.targetProduct), [items])

  return (
    <ResourceCollectionScene
      title={formatMessage({ id: 'navbar.manageRequests' })}
      tabs={tabs}
      actionButtons={[
        {
          label: formatMessage({ id: 'resourceCollections.general.export' }),
          variant: 'outlined',
          onClick: () =>
            openDrawer(
              <DrawerViewExport
                resourceType={ResourceTypes.Response}
                resourceView={tabs?.activeTabParam}
                count={count}
                userFilter={apiFilters}
                exportColumns={getResponseExportColumnsTransparency(hasInquiriesWithProduct)}
                rawExportPayload={basePayload}
                onlyLatest
              />,
            ),
        },
        ...actionButtons,
      ]}
      filter={
        <ProviderResponsesFilters
          implicitFilters={relatedContentFilter}
          allowedFilters={allowedFilters}
          resetPage={resetPage}
        />
      }
    >
      <Table<Response>
        RowComponent={RowComponent}
        rowComponentProps={{ showTargetProductColumn }}
        HeaderComponent={() => (
          <HeaderComponent
            toggleSorting={toggleSorting}
            activeSorting={sorting}
            showTargetProductColumn={showTargetProductColumn}
          />
        )}
        data={items}
        isLoading={isLoading || isFetching}
        count={count}
        isError={isError}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
      />
    </ResourceCollectionScene>
  )
}

export default BaseProviderResponseScene
